import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { NavigationItem } from '../types';
import { Icon } from './Icon';

interface HeaderProps {
  navigation: NavigationItem[];
}

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-1.25rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeaderContainer = styled.header<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transition: all 0.3s ease;
  background: ${props => 
    props.isScrolled 
      ? props.theme.gradients.glass
      : 'transparent'
  };
  backdrop-filter: ${props => props.isScrolled ? 'blur(0.625rem)' : 'none'};
  border-bottom: ${props => 
    props.isScrolled 
      ? `0.0625rem solid ${props.theme.colors.lightGray}50`
      : 'none'
  };
  box-shadow: ${props => 
    props.isScrolled 
      ? props.theme.shadows.md 
      : 'none'
  };
  animation: ${slideDown} 1s ease-out;
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 1rem 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 1rem;
  }
`;

const NavContent = styled.div`
  display: flex;
  items-center;
  justify-content: space-between;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const LogoIcon = styled.div`
  padding: 0.5rem;
  background: ${props => props.theme.gradients.primary};
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoText = styled.span`
  font-family: ${props => props.theme.fonts.heading};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textLight};
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CTAButton = styled.button`
  background: ${props => props.theme.gradients.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.md};
  
  &:hover {
    background: var(--gradient-blue);
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.lg};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  padding: 0.5rem;
  cursor: pointer;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const MobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => props.isOpen ? 'block' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${props => props.theme.gradients.glass};
    backdrop-filter: blur(0.625rem);
    padding: 1rem;
    border-radius: 0 0 0.5rem 0.5rem;
    box-shadow: ${props => props.theme.shadows.lg};
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

const MobileNavLink = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0.75rem 0;
  color: ${props => props.theme.colors.text};
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const MobileCTAButton = styled.button`
  width: 100%;
  margin-top: 1rem;
  background: ${props => props.theme.gradients.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--gradient-blue);
  }
`;

export const Header: React.FC<HeaderProps> = ({ navigation }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    console.log(`Navigation to ${href} clicked`);
    setIsMenuOpen(false);
    
    // If it's an anchor link, scroll to that section
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleCTAClick = () => {
    console.log('CTA button clicked');
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    console.log('Logo clicked - navigate to home');
  };

  return (
    <HeaderContainer isScrolled={isScrolled}>
      <Container>
        <NavContent>
          <LogoContainer onClick={handleLogoClick}>
            <LogoIcon>
              <Icon name="Layers" size={24} />
            </LogoIcon>
            <LogoText>
              Vidrería la Cañadita
            </LogoText>
          </LogoContainer>

          {/* Desktop Navigation */}
          <DesktopNav>
            {navigation.map((item, index) => (
              <NavLink
                key={index}
                onClick={() => handleNavClick(item.href)}
              >
                {item.label}
              </NavLink>
            ))}
            <CTAButton onClick={handleCTAClick}>
              Solicitar Cotización
            </CTAButton>
          </DesktopNav>

          {/* Mobile Menu Button */}
          <MobileMenuButton
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            {isMenuOpen ? <Icon name="X" size={24} /> : <Icon name="Menu" size={24} />}
          </MobileMenuButton>
        </NavContent>

        {/* Mobile Menu */}
        <MobileMenu isOpen={isMenuOpen}>
          {navigation.map((item, index) => (
            <MobileNavLink
              key={index}
              onClick={() => handleNavClick(item.href)}
            >
              {item.label}
            </MobileNavLink>
          ))}
          <MobileCTAButton onClick={handleCTAClick}>
            Solicitar Cotización
          </MobileCTAButton>
        </MobileMenu>
      </Container>
    </HeaderContainer>
  );
};
